import { useTranslation } from 'react-i18next';
import { UserLocale } from 'types/user';

/**
 * Get sorted language options for dropdowns
 */
export function getSortedLanguageOptions() {
  return [
    { value: 'fr-FR', label: 'Français' },
    { value: 'fr-BE', label: 'Français (Belgique)' },
    { value: 'en-US', label: 'English (US)' },
    { value: 'en-AU', label: 'English (Australia)' },
  ];
}

/**
 * Filter countries for selection dropdown (excludes French zones except FR)
 */
export function getCountriesForSelection(
  sortedCountriesAndZones: Array<{
    code: string;
    label: string;
    scholarSystem?: string;
  }>,
) {
  return sortedCountriesAndZones
    .filter(c => c.scholarSystem !== 'FR' || c.code === 'FR')
    .map(c => ({
      value: c.code,
      label: c.label,
    }));
}

/**
 * Hook to get French zones for zone dropdown
 */
export function useGetFrenchZones() {
  const { t } = useTranslation(['countries']);

  return (
    sortedCountriesAndZones: Array<{
      code: string;
      label: string;
      scholarSystem?: string;
    }>,
  ) =>
    sortedCountriesAndZones
      .filter(c => c.scholarSystem === 'FR')
      .map(z =>
        z.code === 'FR' ? { ...z, label: t('countries:FR-metropolitan') } : z,
      );
}

/**
 * Check if a country code is a French zone
 */
export function isFrenchZone(
  countryCode: string,
  sortedFRZones: Array<{ code: string }>,
): boolean {
  return sortedFRZones.some(z => z.code === countryCode);
}

/**
 * Determine the initial country and zone from various sources
 */
export function determineInitialCountryAndZone(
  userCountry?: string,
  fallbackCountry?: string,
  sortedFRZones?: Array<{ code: string }>,
): {
  country: string;
  zone: string | undefined;
} {
  const initialCountry = userCountry || fallbackCountry || 'FR';

  if (
    sortedFRZones &&
    isFrenchZone(initialCountry, sortedFRZones) &&
    initialCountry !== 'FR'
  ) {
    // If the country is actually a French zone, set France as country and the zone
    return { country: 'FR', zone: initialCountry };
  }

  if (initialCountry === 'FR') {
    return { country: initialCountry, zone: 'FR' };
  }

  return { country: initialCountry, zone: undefined };
}

/** This helper is used to ensure safe mapping from the common.locale.long values to UserLocale */
export function getSanitizedUserLocale(locale: string): UserLocale {
  switch (locale) {
    case 'fr-FR':
    case 'en-AU':
    case 'fr-BE':
    case 'en-US':
      return locale as UserLocale;
    default:
      return 'fr-FR';
  }
}
